import { useMemo } from "react";
import type { RecurrenceRule } from "../../../../shared/types";
import { RecurrencePicker } from "./RecurrencePicker";
import { ReminderPicker, type ReminderValue } from "../../../../ui/molecules/ReminderPicker";
import { BellOffIcon } from "../../../../ui/icons";
import { notificationsSupported } from "../../../../shared/hooks/use-notifications";

interface RecurrenceSelectorProps {
  readonly recurrence: RecurrenceRule | null;
  readonly onRecurrenceChange: (rule: RecurrenceRule | null) => void;
  readonly reminderMinutes: number | null;
  readonly reminderTime: string | null;
  readonly reminderType: "BEFORE" | "AT_TIME" | null;
  readonly onReminderChange: (minutes: number | null) => void;
  readonly onReminderTimeChange: (time: string | null) => void;
  readonly onReminderTypeChange: (type: "BEFORE" | "AT_TIME" | null) => void;
  readonly eventDate?: string;
}

export function RecurrenceSelector({
  recurrence,
  onRecurrenceChange,
  reminderMinutes,
  reminderTime,
  reminderType,
  onReminderChange,
  onReminderTimeChange,
  onReminderTypeChange,
  eventDate,
}: RecurrenceSelectorProps) {
  const notifPermission = notificationsSupported && ("Notification" in window ? Notification.permission === "granted" : true);

  // Convertir les valeurs séparées en format ReminderValue pour ReminderPicker
  const reminderValue: ReminderValue = useMemo(() => {
    // Pas de rappel sélectionné
    if (reminderType === null && reminderMinutes === null && reminderTime === null) {
      return null;
    }

    if (reminderType === "AT_TIME") {
      return { type: "at_time", time: reminderTime };
    }
    // BEFORE ou par défaut
    if (reminderType === "BEFORE" || reminderMinutes !== null) {
      return { type: "before", minutes: reminderMinutes };
    }

    return null;
  }, [reminderMinutes, reminderTime, reminderType]);

  function handleReminderChange(value: ReminderValue) {
    if (value === null) {
      onReminderChange(null);
      onReminderTimeChange(null);
      onReminderTypeChange(null);
      return;
    }

    if (value.type === "before") {
      onReminderChange(value.minutes);
      onReminderTypeChange("BEFORE");
      onReminderTimeChange(null);
    } else {
      onReminderChange(null);
      onReminderTypeChange("AT_TIME");
      onReminderTimeChange(value.time);
    }
  }

  return (
    <div className="space-y-3">
      <RecurrencePicker recurrence={recurrence} onChange={onRecurrenceChange} eventDate={eventDate} />

      <div>
        <label className="block text-[10px] font-medium text-ink-faint uppercase tracking-wide mb-1">
          Rappel
        </label>
        <ReminderPicker
          value={reminderValue}
          onChange={handleReminderChange}
        />
        {reminderValue && !notifPermission && (
          <p className="flex items-center gap-1 mt-1 text-[9px] text-amber-400/80">
            <BellOffIcon className="w-2.5 h-2.5 shrink-0" />
            Notifications non activées
          </p>
        )}
      </div>
    </div>
  );
}
